import { apiFetch } from '@crowdex/host-kit/renderer/http';
import {
  type ConnectionField,
  type ConnectionOAuthClientSummary,
  type ConnectionSummary,
  type ConnectionTestResult,
  type ConnectionType,
  type OAuthConnectionAuth,
  renderConnectionTemplate,
} from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { EmptyState, InlineMessage, LoadingState } from '@crowdex/ui/components/feedback';
import { Field } from '@crowdex/ui/components/field';
import { Input } from '@crowdex/ui/components/input';
import { PendingButton } from '@crowdex/ui/components/pending-button';
import {
  ChoiceGroup,
  ChoiceItem,
  SegmentedControl,
  SegmentedControlItem,
} from '@crowdex/ui/components/segmented-control';
import { Textarea } from '@crowdex/ui/components/textarea';
import { ExternalLink, KeyRound, Plus } from 'lucide-react';
import { type FormEvent, type ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { announceConnectionsChanged } from '@/lib/plugins/connection-events';

export type Organization = { organizationId: string; role: 'admin' | 'member' } | null;
export type Listing = { connections: ConnectionSummary[]; organization: Organization };

/** What the user is entering: a new connection of a type, or new values for one. */
export type Editing = {
  type: ConnectionType;
  connection: ConnectionSummary | null;
  /** The way of connecting chosen beforehand (a plugin page's tab). */
  authId?: string | undefined;
};

const message = (error: unknown) => (error instanceof Error ? error.message : String(error));

/** Why a connection cannot be used now, or `null` when it can. */
export function connectionProblem(connection: ConnectionSummary): string | null {
  if (connection.declaration === 'missing') return 'No enabled plugin declares it';
  if (connection.declaration === 'changed') return 'Its plugin changed how it connects: reconnect';
  if (connection.status === 'needs_reauthorization') return 'Reconnect';
  if (connection.status === 'revoked') return 'Revoked';
  return connection.checkError;
}

/** The values the user enters: an api-key's, or those an OAuth sign-in needs first (a server). */
function EnteredValues({
  auth,
  connection,
  formId,
  values,
  setValues,
  test,
}: {
  auth: { fields: readonly ConnectionField[] };
  connection: ConnectionSummary | null;
  formId: string;
  values: Record<string, string>;
  setValues: (values: Record<string, string>) => void;
  test: ConnectionType['test'];
}) {
  // A changed type sends the values elsewhere: every secret is entered again.
  const keepsSecrets = connection !== null && connection.declaration === 'current';
  return (
    <>
      {auth.fields.map((field) => (
        <Field
          description={
            field.secret && keepsSecrets ? 'Leave empty to keep the current one.' : undefined
          }
          id={`${formId}-${field.key}`}
          key={field.key}
          label={field.label}
        >
          {(props) =>
            field.input === 'textarea' ? (
              <Textarea
                {...props}
                onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                placeholder={field.placeholder}
                value={values[field.key] ?? ''}
              />
            ) : (
              <Input
                {...props}
                autoComplete="off"
                onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                placeholder={field.placeholder}
                type={field.input === 'password' ? 'password' : 'text'}
                value={values[field.key] ?? ''}
              />
            )
          }
        </Field>
      ))}
      {test !== undefined && (
        <p className="text-muted-foreground text-xs">
          {checkHost(test.url, values) === null
            ? 'Checked with a request to the address it names.'
            : `Checked against ${checkHost(test.url, values)}.`}
        </p>
      )}
    </>
  );
}

const clientQuery = (type: string, auth: string, owner: 'user' | 'organization') =>
  `/api/connections/oauth/client?${new URLSearchParams({ type, auth, owner })}`;

/**
 * An OAuth grant: the OAuth client the owner registered with the provider (the hub keeps its
 * secret), then a sign-in at the provider in a new window. An MCP server that registers clients
 * itself needs none: the user's own is optional there.
 */
function OAuthSignIn({
  auth,
  type,
  owner,
  organization,
  formId,
  onClient,
}: {
  auth: OAuthConnectionAuth;
  type: ConnectionType;
  owner: 'user' | 'organization';
  organization: Organization;
  formId: string;
  onClient: (ready: boolean) => void;
}) {
  const [client, setClient] = useState<ConnectionOAuthClientSummary | null>(null);
  const [shared, setShared] = useState(false);
  const [editing, setEditing] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The hub registers itself with an MCP server that lets it, or uses the client the server's
  // publisher registered.
  const optional =
    auth.mcpServer !== undefined &&
    (auth.mcpServer.client !== 'registered' || auth.mcpServer.clientId !== undefined);
  const load = useCallback(async () => {
    try {
      const own = await apiFetch<ConnectionOAuthClientSummary>(
        clientQuery(type.id, auth.id, owner),
      );
      // A user without a client of their own uses their organization's.
      const organizationClient =
        !own.configured && owner === 'user' && organization !== null
          ? await apiFetch<ConnectionOAuthClientSummary>(
              clientQuery(type.id, auth.id, 'organization'),
            )
          : null;
      const configured = own.configured || organizationClient?.configured === true;
      setClient(own);
      setShared(organizationClient?.configured === true);
      setEditing(!configured && !optional);
      setClientId(own.clientId ?? '');
      setValues(own.config);
      onClient(configured || optional);
    } catch (reason) {
      setError(message(reason));
    }
  }, [type.id, auth.id, owner, organization, onClient, optional]);
  useEffect(() => {
    void load();
  }, [load]);
  const saveClient = async () => {
    setPending(true);
    setError(null);
    try {
      await apiFetch('/api/connections/oauth/client', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          type: type.id,
          auth: auth.id,
          owner,
          clientId,
          clientSecret,
          values,
        }),
      });
      setClientSecret('');
      await load();
    } catch (reason) {
      setError(message(reason));
    } finally {
      setPending(false);
    }
  };
  if (client === null)
    return error ? <InlineMessage tone="error">{error}</InlineMessage> : <LoadingState />;
  if (!editing)
    return (
      <p className="text-muted-foreground text-xs">
        {client.configured
          ? `OAuth client ${client.clientId}.`
          : shared
            ? "Uses your organization's OAuth client."
            : 'No OAuth app to register: Crowdex registers itself with the server.'}{' '}
        <button
          className="underline underline-offset-2"
          onClick={() => setEditing(true)}
          type="button"
        >
          {client.configured ? 'Change' : 'Use your own OAuth app'}
        </button>
      </p>
    );
  return (
    <fieldset className="space-y-3 rounded-xl border p-3">
      <legend className="px-1 font-medium text-xs">OAuth client</legend>
      <p className="text-muted-foreground text-xs">
        Register an OAuth app with {type.title}
        {auth.clientSetupUrl ? (
          <>
            {' '}
            (
            <a className="underline" href={auth.clientSetupUrl} rel="noreferrer" target="_blank">
              where
            </a>
            )
          </>
        ) : null}{' '}
        and give it this redirect URI:
      </p>
      <Input aria-label="Redirect URI" readOnly value={client.redirectUri} />
      <Field id={`${formId}-client-id`} label="Client ID">
        {(props) => (
          <Input
            {...props}
            onChange={(event) => setClientId(event.target.value)}
            value={clientId}
          />
        )}
      </Field>
      {(auth.tokenAuth !== 'none' || auth.mcpServer !== undefined) && (
        <Field
          description={
            client.hasSecret
              ? 'Leave empty to keep the current one.'
              : auth.tokenAuth === 'none'
                ? 'Leave empty for a public client.'
                : undefined
          }
          id={`${formId}-client-secret`}
          label="Client secret"
        >
          {(props) => (
            <Input
              {...props}
              autoComplete="off"
              onChange={(event) => setClientSecret(event.target.value)}
              type="password"
              value={clientSecret}
            />
          )}
        </Field>
      )}
      {auth.clientFields.map((field) => (
        <Field id={`${formId}-client-${field.key}`} key={field.key} label={field.label}>
          {(props) => (
            <Input
              {...props}
              autoComplete="off"
              onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
              placeholder={field.placeholder}
              type={field.secret ? 'password' : 'text'}
              value={values[field.key] ?? ''}
            />
          )}
        </Field>
      ))}
      {error && <InlineMessage tone="error">{error}</InlineMessage>}
      <div className="flex justify-end">
        <PendingButton
          disabled={clientId.trim() === ''}
          onClick={() => void saveClient()}
          pending={pending}
          pendingLabel="Saving…"
          size="sm"
          type="button"
          variant="outline"
        >
          Save client
        </PendingButton>
      </div>
    </fieldset>
  );
}

/**
 * Connecting an account (D33, phone 30): a short dialog — its name, who may use it, the way of
 * connecting — then the provider's page in the browser, or the key the user pastes.
 */
export function ConnectionForm({
  editing,
  organization,
  onClose,
  onSaved,
  logo = null,
}: {
  editing: Editing;
  organization: Organization;
  onClose: () => void;
  onSaved: () => void;
  /** The service's logo, beside the title. */
  logo?: ReactNode;
}) {
  const formId = useId();
  const { type, connection } = editing;
  const [authId, setAuthId] = useState(
    connection?.authId ?? editing.authId ?? type.auth[0]?.id ?? '',
  );
  const auth = type.auth.find((item) => item.id === authId) ?? type.auth[0];
  const [name, setName] = useState(connection?.name ?? type.title);
  const [owner, setOwner] = useState<'user' | 'organization'>(connection?.owner.kind ?? 'user');
  const [values, setValues] = useState<Record<string, string>>(connection?.config ?? {});
  const [clientReady, setClientReady] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      if (auth?.kind === 'oauth2') {
        const started = await apiFetch<{ url: string } | { connection: ConnectionSummary }>(
          '/api/connections/oauth/start',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              type: type.id,
              auth: auth.id,
              ...(connection === null ? { name, owner } : { connection: connection.id }),
              ...(auth.fields.length > 0 ? { values } : {}),
            }),
          },
        );
        // Connected with the user's Crowlingo account: nothing to authorize.
        if ('connection' in started) {
          onSaved();
          return;
        }
        // The provider's page opens beside the service; its callback lands on crowlink.
        window.open(started.url, '_blank', 'noopener,noreferrer');
        setSigningIn(true);
      } else if (connection === null) {
        await apiFetch('/api/connections', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ type: type.id, name, owner, auth: auth?.id, values }),
        });
        onSaved();
      } else {
        await apiFetch(`/api/connections/${connection.id}`, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name, values }),
        });
        onSaved();
      }
    } catch (reason) {
      setError(message(reason));
    } finally {
      setPending(false);
    }
  };
  const onClient = useCallback((ready: boolean) => setClientReady(ready), []);
  const oauth = auth?.kind === 'oauth2';
  const methodLabel = (item: (typeof type.auth)[number]) =>
    item.label ?? (item.kind === 'oauth2' ? 'Authorize access' : 'API key');
  const methodDetail = (item: (typeof type.auth)[number]) =>
    item.kind === 'oauth2' ? `Sign in to ${type.title} in your browser` : 'Paste a personal key';
  return (
    <Dialog open onOpenChange={(open) => !open && (signingIn ? onSaved() : onClose())}>
      <DialogContent
        className="gap-4 rounded-2xl pointer-coarse:translate-none pointer-coarse:px-5! sm:max-w-md"
        data-sheet
      >
        <div className="flex items-center gap-3 pr-8">
          {logo}
          <div className="min-w-0">
            <DialogTitle>
              {connection === null ? `Connect ${type.title}` : `Reconnect ${connection.name}`}
            </DialogTitle>
            <DialogDescription className="mt-0.5">
              {type.description ?? `An account on ${type.title}`}
            </DialogDescription>
          </div>
        </div>
        {signingIn ? (
          <div className="space-y-3">
            <InlineMessage tone="info">
              Authorize Crowdex's access to {type.title} in the window that opened: you come back
              here automatically.
            </InlineMessage>
            <div className="flex justify-end">
              <Button className="rounded-control" onClick={onSaved} type="button">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            {(connection === null || auth?.kind === 'api-key') && (
              <Field id={`${formId}-name`} label="Name">
                {(props) => (
                  <Input
                    {...props}
                    className="h-10 rounded-xl"
                    onChange={(event) => setName(event.target.value)}
                    value={name}
                  />
                )}
              </Field>
            )}
            {connection === null && organization?.role === 'admin' && (
              <div className="space-y-1.5">
                <p className="font-medium text-sm">For</p>
                <SegmentedControl
                  aria-label="Who can use it"
                  className="flex w-full"
                  onValueChange={(value) => setOwner(value as 'user' | 'organization')}
                  value={owner}
                >
                  <SegmentedControlItem value="user">Me</SegmentedControlItem>
                  <SegmentedControlItem value="organization">My organization</SegmentedControlItem>
                </SegmentedControl>
                <p className="text-muted-foreground text-xs">
                  {owner === 'organization'
                    ? 'Members of your organization can use it in their conversations.'
                    : 'Only your conversations and automations use it.'}
                </p>
              </div>
            )}
            {connection === null && editing.authId === undefined && type.auth.length > 1 && (
              <fieldset className="space-y-1.5">
                <legend className="mb-1.5 font-medium text-sm">Way of connecting</legend>
                <ChoiceGroup
                  aria-label="Way of connecting"
                  className="space-y-2"
                  onValueChange={setAuthId}
                  value={authId}
                >
                  {type.auth.map((item) => {
                    const selected = item.id === authId;
                    return (
                      <ChoiceItem
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                          selected
                            ? 'border-foreground/40 bg-foreground/5'
                            : 'border-foreground/12 hover:bg-foreground/4'
                        }`}
                        key={item.id}
                        value={item.id}
                      >
                        <span
                          aria-hidden
                          className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${
                            selected ? 'border-foreground' : 'border-foreground/35'
                          }`}
                        >
                          {selected && <span className="size-2 rounded-full bg-foreground" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-medium text-sm">{methodLabel(item)}</span>
                          <span className="block text-muted-foreground text-xs">
                            {methodDetail(item)}
                          </span>
                        </span>
                      </ChoiceItem>
                    );
                  })}
                </ChoiceGroup>
              </fieldset>
            )}
            {oauth && auth.kind === 'oauth2' ? (
              <>
                {auth.fields.length > 0 && (
                  <EnteredValues
                    auth={auth}
                    connection={connection}
                    formId={formId}
                    setValues={setValues}
                    test={undefined}
                    values={values}
                  />
                )}
                <OAuthSignIn
                  auth={auth}
                  formId={formId}
                  onClient={onClient}
                  organization={organization}
                  owner={owner}
                  type={type}
                />
              </>
            ) : auth !== undefined ? (
              <EnteredValues
                auth={auth}
                connection={connection}
                formId={formId}
                setValues={setValues}
                test={type.test}
                values={values}
              />
            ) : null}
            {error && <InlineMessage tone="error">{error}</InlineMessage>}
            <div className="-mx-dialog flex items-center gap-2 border-hairline border-t px-dialog pt-4">
              <p className="min-w-0 flex-1 text-muted-foreground text-xs">
                {oauth ? 'You come back here automatically.' : ''}
              </p>
              <Button className="rounded-control" onClick={onClose} type="button" variant="outline">
                Cancel
              </Button>
              <PendingButton
                disabled={oauth && !clientReady}
                pending={pending}
                pendingLabel={oauth ? 'Opening…' : 'Saving…'}
                type="submit"
              >
                {oauth ? (
                  <>
                    Continue in browser <ExternalLink aria-hidden className="size-3.5" />
                  </>
                ) : (
                  'Save'
                )}
              </PendingButton>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Where a check goes, so the user sees where their values are sent; `null` until known. */
function checkHost(template: string, values: Readonly<Record<string, string>>): string | null {
  try {
    return new URL(renderConnectionTemplate(template, values)).host || null;
  } catch {
    return null;
  }
}

function ConnectionRow({
  connection,
  type,
  onEdit,
  onChanged,
}: {
  connection: ConnectionSummary;
  type: ConnectionType | undefined;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const problem = connectionProblem(connection);
  const act = async (name: string, run: () => Promise<unknown>) => {
    setPending(name);
    setError(null);
    try {
      await run();
      onChanged();
    } catch (reason) {
      setError(message(reason));
    } finally {
      setPending(null);
    }
  };
  return (
    <li className="space-y-2 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <KeyRound aria-hidden className="size-4 text-muted-foreground" />
        <span className="font-medium text-sm">{connection.name}</span>
        {connection.isDefault && (
          <span className="rounded-sm bg-accent px-2 py-0.5 text-xs">Default</span>
        )}
        {connection.owner.kind === 'organization' && (
          <span className="rounded-sm border px-2 py-0.5 text-muted-foreground text-xs">
            Organization
          </span>
        )}
        <span
          className={`text-xs ${problem === null ? 'text-muted-foreground' : 'text-destructive'}`}
        >
          {problem ?? (connection.checkedAt === null ? 'Not checked' : 'Working')}
        </span>
        {connection.account === 'crowlingo' && (
          <span className="text-muted-foreground text-xs">With your Crowlingo account</span>
        )}
        <span className="ml-auto flex flex-wrap gap-1">
          {type?.test !== undefined && connection.declaration === 'current' && (
            <PendingButton
              onClick={() =>
                act('test', () =>
                  apiFetch<{ result: ConnectionTestResult }>(
                    `/api/connections/${connection.id}/test`,
                    { method: 'POST' },
                  ),
                )
              }
              pending={pending === 'test'}
              size="xs"
              variant="ghost"
            >
              Check
            </PendingButton>
          )}
          {connection.canEdit && !connection.isDefault && (
            <PendingButton
              onClick={() =>
                act('default', () =>
                  apiFetch(`/api/connections/${connection.id}/default`, { method: 'PUT' }),
                )
              }
              pending={pending === 'default'}
              size="xs"
              variant="ghost"
            >
              Make default
            </PendingButton>
          )}
          {connection.canEdit && type !== undefined && (
            <Button onClick={onEdit} size="xs" variant="ghost">
              {connection.declaration === 'changed' ||
              connection.status === 'needs_reauthorization' ||
              connection.authKind === 'oauth2'
                ? 'Reconnect'
                : 'Edit'}
            </Button>
          )}
          {connection.canEdit && (
            <PendingButton
              onClick={() =>
                act('remove', () =>
                  apiFetch(`/api/connections/${connection.id}`, { method: 'DELETE' }),
                )
              }
              pending={pending === 'remove'}
              size="xs"
              variant="ghost"
            >
              Remove
            </PendingButton>
          )}
        </span>
      </div>
      {error && (
        <InlineMessage compact tone="error">
          {error}
        </InlineMessage>
      )}
    </li>
  );
}

/**
 * The connection hub's accounts (`docs/adr-connections-hub.md`): the types the enabled plugins
 * declare, each with the user's and their organization's connections; one service's only with
 * `service`, inside its plugin's page, and one way of connecting with `method` (a tab there).
 */
export function HubConnections({
  service,
  method,
  onChanged,
}: {
  service?: string;
  method?: string;
  /** Called after the user adds, edits or removes an account here. */
  onChanged?: () => void;
} = {}) {
  const [types, setTypes] = useState<ConnectionType[] | null>(null);
  const [listing, setListing] = useState<Listing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const load = useCallback(async () => {
    try {
      const [nextTypes, nextListing] = await Promise.all([
        apiFetch<ConnectionType[]>('/api/connections/types'),
        apiFetch<Listing>('/api/connections'),
      ]);
      setTypes(nextTypes);
      setListing(nextListing);
      setError(null);
    } catch (reason) {
      setError(message(reason));
    }
  }, []);
  useEffect(() => {
    void load();
    // A sign-in finishes in another window: the list follows when the user comes back.
    const reload = () => void load();
    window.addEventListener('focus', reload);
    return () => window.removeEventListener('focus', reload);
  }, [load]);
  if (error !== null) return <InlineMessage tone="error">{error}</InlineMessage>;
  if (types === null || listing === null) return <LoadingState>Loading connections…</LoadingState>;
  const shown = types.filter((type) => service === undefined || type.id === service);
  const declared = new Set(types.map((type) => type.id));
  const undeclared = listing.connections.filter(
    (connection) =>
      !declared.has(connection.type) && (service === undefined || connection.type === service),
  );
  if (service !== undefined && shown.length === 0 && undeclared.length === 0)
    return (
      <p className="text-muted-foreground text-sm" role="status">
        Enable this service to connect an account.
      </p>
    );
  if (shown.length === 0 && undeclared.length === 0)
    return (
      <EmptyState
        description="Plugins that use an account on another service declare it; enable one to connect it here."
        title="No plugin declares a connection"
      />
    );
  const groups: Array<{ id: string; title: string; type: ConnectionType | undefined }> = [
    ...shown.map((type) => ({ id: type.id, title: type.title, type })),
    ...[...new Set(undeclared.map((connection) => connection.type))].map((id) => ({
      id,
      title: id,
      type: undefined,
    })),
  ];
  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const connections = listing.connections.filter(
          (connection) =>
            connection.type === group.id && (method === undefined || connection.authId === method),
        );
        const type = group.type;
        const auth = type?.auth.find((item) => item.id === method);
        if (method !== undefined && type !== undefined && auth === undefined)
          return (
            <p className="text-muted-foreground text-xs" key={group.id} role="status">
              This way of connecting is not available yet.
            </p>
          );
        return (
          <section className="rounded-xl border" key={group.id}>
            <header className="flex items-center gap-3 border-b px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-sm">
                  {method === undefined ? group.title : 'Connections'}
                </h3>
                {type === undefined ? (
                  <p className="truncate text-muted-foreground text-xs">
                    No enabled plugin declares this type any more.
                  </p>
                ) : method === undefined && type.description ? (
                  <p className="truncate text-muted-foreground text-xs">{type.description}</p>
                ) : null}
              </div>
              {type !== undefined && (
                <Button
                  onClick={() => setEditing({ type, connection: null, authId: auth?.id })}
                  size="sm"
                  variant="outline"
                >
                  <Plus /> Connect
                </Button>
              )}
            </header>
            {connections.length === 0 ? (
              <p className="px-3 py-2.5 text-muted-foreground text-xs">No account connected.</p>
            ) : (
              <ul className="divide-y">
                {connections.map((connection) => (
                  <ConnectionRow
                    connection={connection}
                    key={connection.id}
                    onChanged={() => {
                      void load();
                      announceConnectionsChanged();
                      onChanged?.();
                    }}
                    onEdit={() => type && setEditing({ type, connection })}
                    type={type}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
      {editing !== null && (
        <ConnectionForm
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
            announceConnectionsChanged();
            onChanged?.();
          }}
          organization={listing.organization}
        />
      )}
    </div>
  );
}
